import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { matrixApi, userApi } from '../lib/api';
import { Pencil, X, Check, AlertTriangle, Tag } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface Column {
  appId: string;
  appName: string;
  levelName: string;
  type: 'group' | 'attribute';
  cognitoGroupName?: string;
  attributeKey?: string;
  attributeValue?: string;
}

interface Row {
  userId: string;
  username: string;
  email: string;
  givenName: string;
  familyName: string;
  groups: string[];
  customAttributes: Record<string, string>;
}


interface PendingChange {
  username: string;
  displayName: string;
  email: string;
  appName: string;
  levelName: string;
  cognitoGroupName: string;
  action: 'add' | 'remove';
}

export default function Matrix() {
  const queryClient = useQueryClient();

  const [appFilter, setAppFilter]   = useState('');
  const [userFilter, setUserFilter] = useState('');

  // Edit mode
  const [editMode, setEditMode]                     = useState(false);
  // localGroups: username → Set of cognitoGroupNames (mutable copy during edit)
  const [localGroups, setLocalGroups]               = useState<Record<string, Set<string>>>({});
  // originalGroups: frozen snapshot at edit-start for diff computation
  const [originalGroups, setOriginalGroups]         = useState<Record<string, Set<string>>>({});
  // Confirmation modal
  const [showConfirm, setShowConfirm]               = useState(false);
  const [saving, setSaving]                         = useState(false);

  const { data: matrix, isLoading, error } = useQuery({
    queryKey: ['matrix'],
    queryFn: matrixApi.get,
    staleTime: 60_000,
  });



  const columns: Column[] = useMemo(() => matrix?.columns ?? [], [matrix]);
  const rows: Row[]       = useMemo(() => matrix?.rows    ?? [], [matrix]);

  // Filtered columns (by app)
  const visibleColumns = appFilter
    ? columns.filter(c => c.appId === appFilter)
    : columns;

  // Group visible columns by app for header spanning
  const appGroups: { appId: string; appName: string; cols: Column[] }[] = [];
  for (const col of visibleColumns) {
    const existing = appGroups.find(a => a.appId === col.appId);
    if (existing) existing.cols.push(col);
    else appGroups.push({ appId: col.appId, appName: col.appName, cols: [col] });
  }

  // Filtered rows (by user)
  const filteredRows = rows.filter(r =>
    !userFilter ||
    r.email.toLowerCase().includes(userFilter.toLowerCase()) ||
    `${r.givenName} ${r.familyName}`.toLowerCase().includes(userFilter.toLowerCase())
  );

  // ── Edit mode helpers ────────────────────────────────────────────
  const enterEditMode = () => {
    const snap: Record<string, Set<string>> = {};
    for (const r of rows) snap[r.username] = new Set(r.groups);
    setOriginalGroups(snap);
    // deep copy for mutable local state
    const local: Record<string, Set<string>> = {};
    for (const r of rows) local[r.username] = new Set(r.groups);
    setLocalGroups(local);
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setLocalGroups({});
    setOriginalGroups({});
    setShowConfirm(false);
  };

  const toggleCell = (username: string, cognitoGroupName: string) => {
    setLocalGroups(prev => {
      const next = { ...prev };
      const userSet = new Set(next[username] ?? []);
      if (userSet.has(cognitoGroupName)) userSet.delete(cognitoGroupName);
      else userSet.add(cognitoGroupName);
      next[username] = userSet;
      return next;
    });
  };

  // Compute diff — only group-type columns are editable
  const pendingChanges: PendingChange[] = useMemo(() => {
    if (!editMode) return [];
    const changes: PendingChange[] = [];
    for (const row of rows) {
      const orig  = originalGroups[row.username] ?? new Set<string>();
      const local = localGroups[row.username]    ?? new Set<string>();
      for (const col of columns) {
        if ((col.type ?? 'group') !== 'group' || !col.cognitoGroupName) continue;
        const wasOn = orig.has(col.cognitoGroupName);
        const isOn  = local.has(col.cognitoGroupName);
        if (wasOn && !isOn) {
          changes.push({
            username: row.username,
            displayName: `${row.givenName} ${row.familyName}`.trim() || row.username,
            email: row.email,
            appName: col.appName,
            levelName: col.levelName,
            cognitoGroupName: col.cognitoGroupName,
            action: 'remove',
          });
        } else if (!wasOn && isOn) {
          changes.push({
            username: row.username,
            displayName: `${row.givenName} ${row.familyName}`.trim() || row.username,
            email: row.email,
            appName: col.appName,
            levelName: col.levelName,
            cognitoGroupName: col.cognitoGroupName,
            action: 'add',
          });
        }
      }
    }
    return changes;
  }, [editMode, originalGroups, localGroups, rows, columns]);

  const applyChanges = async () => {
    setSaving(true);
    let failed = 0;
    for (const c of pendingChanges) {
      try {
        if (c.action === 'add') {
          await userApi.addToGroup(c.username, c.cognitoGroupName);
        } else {
          await userApi.removeFromGroup(c.username, c.cognitoGroupName);
        }
      } catch {
        failed++;
      }
    }
    setSaving(false);
    setShowConfirm(false);
    setEditMode(false);
    setLocalGroups({});
    setOriginalGroups({});
    await queryClient.invalidateQueries({ queryKey: ['matrix'] });
    if (failed > 0) toast.error(`${failed} cambio(s) fallaron al aplicarse.`);
    else toast.success('Matriz guardada correctamente.');
  };

  // ── Render ───────────────────────────────────────────────────────
  if (isLoading) return <p className="text-sm text-muted-foreground p-6">Cargando asignaciones...</p>;
  if (error)     return <p className="text-sm text-destructive p-6">Error al cargar la matriz.</p>;

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-foreground">Asignaciones</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {editMode
              ? 'Modo edición: tildá o destildá celdas y luego guardá los cambios.'
              : 'Permisos de acceso por usuario y aplicación.'}
          </p>
        </div>
        <div className="flex gap-2">
          {!editMode ? (
            <button onClick={enterEditMode} className={btnSecondary}>
              <Pencil className="h-4 w-4 mr-2" />Editar matriz
            </button>
          ) : (
            <>
              <button onClick={cancelEdit} disabled={saving} className={btnSecondary}>
                <X className="h-4 w-4 mr-2" />Cancelar
              </button>
              <button
                onClick={() => setShowConfirm(true)}
                disabled={pendingChanges.length === 0 || saving}
                className={btnPrimary}
              >
                <Check className="h-4 w-4 mr-2" />
                Guardar matriz{pendingChanges.length > 0 ? ` (${pendingChanges.length})` : ''}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Edit mode banner */}
      {editMode && (
        <div className="flex items-center gap-2 px-4 py-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400 text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            Estás en modo edición.{' '}
            {pendingChanges.length === 0
              ? 'Sin cambios pendientes.'
              : `${pendingChanges.length} cambio${pendingChanges.length !== 1 ? 's' : ''} pendiente${pendingChanges.length !== 1 ? 's' : ''}.`}
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <select
          value={appFilter}
          onChange={e => setAppFilter(e.target.value)}
          className={cn(inputCls, 'sm:w-56')}
        >
          <option value="">Todas las aplicaciones</option>
          {/* Unique apps from all columns (not just visible) */}
          {Array.from(new Map(columns.map(c => [c.appId, c.appName])).entries()).map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Filtrar usuarios..."
          value={userFilter}
          onChange={e => setUserFilter(e.target.value)}
          className={cn(inputCls, 'sm:w-64')}
        />
      </div>

      {/* Empty state */}
      {columns.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-10 text-center shadow-sm">
          <p className="text-muted-foreground text-sm">
            No hay aplicaciones definidas. Primero creá aplicaciones en la sección Aplicaciones.
          </p>
        </div>
      ) : (
        <>
          {/* Matrix table */}
          <div className={cn(
            'bg-card border rounded-lg overflow-x-auto shadow-sm transition-colors',
            editMode ? 'border-amber-500/40' : 'border-border'
          )}>
            <table key={appFilter} className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th
                    rowSpan={2}
                    className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase bg-muted sticky left-0 z-10 border-r border-border"
                  >
                    Usuario
                  </th>
                  {appGroups.map(ag => (
                    <th
                      key={ag.appId}
                      colSpan={ag.cols.length}
                      className="px-4 py-2 text-center text-xs font-semibold text-foreground bg-accent/10 border-r border-border"
                    >
                      {ag.appName}
                    </th>
                  ))}
                </tr>
                <tr className="border-b border-border">
                  {visibleColumns.map(col => (
                    <th
                      key={`${col.appId}-${col.levelName}`}
                      title={col.type === 'attribute'
                        ? `${col.attributeKey} = ${col.attributeValue}`
                        : col.cognitoGroupName}
                      className="px-3 py-2 text-center text-xs font-medium text-muted-foreground bg-muted whitespace-nowrap border-r border-border/50"
                    >
                      <div className="flex flex-col items-center gap-0.5">
                        {col.levelName}
                        {col.type === 'attribute' && (
                          <Tag className="h-2.5 w-2.5 text-accent" />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 1} className="px-4 py-8 text-center text-muted-foreground">
                      No se encontraron usuarios
                    </td>
                  </tr>
                ) : (
                  filteredRows.map(row => (
                    <tr key={row.userId} className="hover:bg-muted/40 transition-colors">
                      <td className="px-4 py-3 sticky left-0 bg-card border-r border-border z-10 whitespace-nowrap">
                        <div className="font-medium text-foreground">
                          {row.givenName} {row.familyName}
                        </div>
                        <div className="text-xs text-muted-foreground">{row.email}</div>
                      </td>
                      {visibleColumns.map(col => {
                        const isAttr = (col.type ?? 'group') === 'attribute';

                        let hasAccess: boolean;
                        if (isAttr) {
                          hasAccess = !!col.attributeKey &&
                            (row.customAttributes ?? {})[col.attributeKey] === col.attributeValue;
                        } else {
                          const groupSet = editMode
                            ? (localGroups[row.username] ?? new Set(row.groups))
                            : new Set(row.groups);
                          hasAccess = groupSet.has(col.cognitoGroupName ?? '');
                        }

                        const wasAccess = isAttr ? hasAccess : (editMode
                          ? (originalGroups[row.username] ?? new Set(row.groups)).has(col.cognitoGroupName ?? '')
                          : hasAccess);
                        const changed = !isAttr && editMode && hasAccess !== wasAccess;

                        return (
                          <td
                            key={`${row.userId}-${col.type}-${col.levelName}`}
                            className={cn(
                              'px-3 py-3 text-center border-r border-border/50 transition-colors',
                              changed && 'bg-amber-500/10'
                            )}
                          >
                            {isAttr ? (
                              <span className={cn(
                                'inline-block h-3 w-3 rounded-full',
                                hasAccess ? 'bg-accent' : 'bg-muted-foreground/20'
                              )} title={hasAccess ? 'Atributo coincide' : 'Atributo no coincide'} />
                            ) : editMode ? (
                              <input
                                type="checkbox"
                                checked={hasAccess}
                                onChange={() => col.cognitoGroupName && toggleCell(row.username, col.cognitoGroupName)}
                                className="h-4 w-4 rounded border-input cursor-pointer accent-primary"
                              />
                            ) : (
                              <span className={cn(
                                'inline-block h-3 w-3 rounded-full',
                                hasAccess ? 'bg-green-500' : 'bg-muted-foreground/20'
                              )} />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-muted-foreground">
            {filteredRows.length} usuario{filteredRows.length !== 1 ? 's' : ''} · {visibleColumns.length} permiso{visibleColumns.length !== 1 ? 's' : ''}
          </p>
        </>
      )}

      {/* ── Confirm changes modal ─────────────────────────────────── */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !saving && setShowConfirm(false)} />
          <div className="relative bg-card border border-border rounded-lg shadow-2xl w-full max-w-lg mx-4 flex flex-col max-h-[90vh]">

            <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
              <h3 className="text-base font-semibold text-foreground">Confirmar cambios</h3>
              {!saving && (
                <button onClick={() => setShowConfirm(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              <p className="text-sm text-muted-foreground mb-4">
                Se van a aplicar los siguientes {pendingChanges.length} cambio{pendingChanges.length !== 1 ? 's' : ''}:
              </p>
              <div className="space-y-2">
                {pendingChanges.map((c, i) => (
                  <div
                    key={i}
                    className={cn(
                      'flex items-start gap-3 px-3 py-2 rounded-md text-sm border',
                      c.action === 'add'
                        ? 'bg-green-500/10 border-green-500/20 text-green-700 dark:text-green-400'
                        : 'bg-destructive/10 border-destructive/20 text-destructive'
                    )}
                  >
                    <span className="font-bold shrink-0 mt-0.5">{c.action === 'add' ? '+' : '−'}</span>
                    <div>
                      <span className="font-medium">{c.displayName}</span>
                      <span className="text-muted-foreground mx-1">·</span>
                      <span>{c.appName} / {c.levelName}</span>
                      <div className="text-xs opacity-70 mt-0.5">{c.email}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 px-5 py-4 border-t border-border shrink-0">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                disabled={saving}
                className={btnSecondary}
              >
                Volver
              </button>
              <button
                onClick={applyChanges}
                disabled={saving}
                className={btnPrimary}
              >
                {saving ? 'Aplicando...' : 'Aplicar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = 'block w-full border border-input bg-background text-foreground rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const btnPrimary = 'inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50';
const btnSecondary = 'inline-flex items-center px-4 py-2 border border-border text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted transition-colors disabled:opacity-50';
